import { useEffect, useState } from "react";
import { ToastTypes } from "../contexts/toastr.context";
import { history } from "../_config";
import {
  getUserIdFromStorage,
  getUserTokenFromStorage,
} from "../_config/helper.config.js";
import { createPostDetailPageRoute } from "../_pages/post-detail.page";
import { store } from "../_store";
import { addKarmaPointAction } from "../_store/community-karma-points.store/karma-points.action";
import {
  postDetailHandleAddComment,
  postDetailHandleCommentReactionUpdateFromSocket,
  postDetailHandleLikeIncrement,
  postDetailHandleRemoveComment,
} from "../_store/post-detail.store/post-detail-realtime-event-handler";
import { increaseNewPostCount } from "../_store/sections.store";
import {
  triggerSectionPostCommentAddAction,
  triggerSectionPostCommentReactionUpdateAction,
  triggerSectionPostCommentRemoveAction,
  triggerSectionPostLikeIncrementAction,
} from "../_store/sections.store/sections-realtime-reducer";
import { setCommunity } from "../_store/_actions/community.actions";
import MainUtil from "../_utils/main.util";

const SOCKET_EVENTS = {
  REALTIME: {
    POST_CREATE: "REALTIME:POST_CREATE",
    POST_DELETE: "REALTIME:POST_DELETE",
    POST_LIKE: "REALTIME:POST_LIKE",
    POST_COMMENT_ADD: "REALTIME:POST_COMMENT_ADD",
    POST_COMMENT_REMOVE: "REALTIME:POST_COMMENT_REMOVE",
    POST_COMMENT_REACTION_UPDATE: "REALTIME:POST_COMMENT_REACTION_UPDATE",
    GROUP_UPDATE: "REALTIME:GROUP_UPDATE",
    SECTION_UPDATE: "REALTIME:SECTION_UPDATE",
    KARMAPOINT_INCREMENT: "REALTIME:KARMAPOINT_INCREMENT",
  },
  LOGOUT: "LOGOUT",
};

export default function useSocketListeners({
  socket,
  addToastWithAction,
  addNewNotification,
  community,
  chats,
  setChats,
}) {
  const token = getUserTokenFromStorage();

  // get selected chat user id from url
  const [userId, setUserId] = useState(window.location.pathname.split("/")[2]);

  const communityId = community ? community.id : null;

  const authUserRoleInCommunity = community?.myRole;

  history.listen((location, action) => {
    // set the current chat userId if needed, this will be used to not show unread messages
    setUserId(location.pathname.split("/")[2]);
  });

  // initialize main socket events
  useEffect(() => {
    if (socket && token) {
      // listen for connect event
      const connectListenerWithSocket = addSocketToListener(
        socket,
        connectListener,
        { token, communityId }
      );
      const registeredUserListenerWithSocket = addSocketToListener(
        socket,
        registeredUserListener
      );
      const notificationListenerWithSocket = addSocketToListener(
        socket,
        notificationListener,
        {
          addToastWithAction,
          addNewNotification,
        }
      );

      const testListener = (data) => {
        // console.log("Test listener triggered", data);
      };

      socket.on("connect", connectListenerWithSocket);
      socket.on("registered-user", registeredUserListenerWithSocket);
      socket.on("notification", notificationListenerWithSocket);
      socket.on("test", testListener);

      const listeners = addListenersToSocket(socket);

      return () => {
        socket.off("connect", connectListenerWithSocket);
        socket.off("registered-user", registeredUserListenerWithSocket);
        socket.off("notification", notificationListenerWithSocket);
        socket.off("test", testListener);

        removeListenersFromSocket(socket, listeners);
      };
    }
  }, [socket]);

  // initialize direct message related events
  useEffect(() => {
    if (socket) {
      // listen for message received event
      const messageReceivedListenerWithSocket = addSocketToListener(
        socket,
        messageReceivedListener,
        {
          chats,
          setChats,
          userId,
        }
      );
      const newChatListenerWithSocket = addSocketToListener(
        socket,
        newChatListener,
        {
          chats,
          setChats,
          userId,
        }
      );
      if (chats === undefined) {
        return;
      }
      // order chats
      chats.forEach((chatUser) => {
        if (chatUser === undefined) {
          return;
        }
        socket.on(
          `direct-message:${chatUser.id}:message-received`,
          messageReceivedListenerWithSocket
        );
      });

      // new chat
      socket.on("direct-message:new-chat", newChatListenerWithSocket);

      return () => {
        chats.forEach((chatUser) => {
          if (chatUser === undefined) {
            return;
          }
          socket.off(
            `direct-message:${chatUser.id}:message-received`,
            messageReceivedListenerWithSocket
          );
        });
        socket.off("direct-message:new-chat", newChatListenerWithSocket);
      };
    }
  }, [socket, userId, chats]);

  //   community user related
  useEffect(() => {
    if (socket && communityId) {
      const communityUserCountChangeListenerWithSocket = addSocketToListener(
        socket,
        (socket, data) => {
          store.dispatch(
            setCommunity({
              ...community,
              newUserCount: community.newUserCount
                ? community.newUserCount
                : 0 + data.count,
            })
          );
        }
      );
      socket.on(
        `community:${communityId}:user-count-change`,
        communityUserCountChangeListenerWithSocket
      );

      return () => {
        socket.off(
          `community:${communityId}:user-count-change`,
          communityUserCountChangeListenerWithSocket
        );
      };
    }
  }, [socket, userId, communityId, authUserRoleInCommunity]);
}

// socket mapper
const addSocketToListener = (socket, listener, extraData = {}) => {
  return (data) => {
    if (["development", "uat"].includes(process.env.REACT_APP_ENV)) {
      // console.log("socket data", data);
    }
    listener(socket, data, extraData);
  };
};

// listeners
const connectListener = (socket, data, extraData) => {
  // try to register the user
  socket.emit("register-user", {
    token: extraData.token,
    communityId: extraData.communityId,
  });
};

const registeredUserListener = (socket, data, extraData) => {
  // console.log("Socket: registered with system!");
};

const notificationListener = (socket, data, extraData) => {
  // console.log("Event received: notification!");
  // add toast
  // show post title for now
  extraData.addToastWithAction({
    title: data.notification.title,
    message: data.notification.post.title,
    type: ToastTypes.info,
    time: 5000,
    onClick: (removeToast) => {
      history.push(createPostDetailPageRoute(data.notification.post.id));
      removeToast();
    },
  });
  // add notification to notification list
  extraData.addNewNotification({ ...data.notification, isViewed: false });
};

// direct message related
const messageReceivedListener = (
  socket,
  { message },
  {
    chats,
    setChats,
    userId, // active user id
  }
) => {
  // if message.sender.id is in chats, update its count
  // console.log("Socket: message received listener called!", message);
  const chatAlreadyExists = chats.find((chat) => chat.id === message.sender.id);
  if (chatAlreadyExists && userId !== message.sender.id) {
    // find that chat and update its count, if not on that specific chat
    const chat = chats.find((chat) => chat.id === message.sender.id);
    chat.unreadMessageCount = chat.unreadMessageCount
      ? chat.unreadMessageCount + 1
      : 1;
    setChats([...chats]);
  }
};

const newChatListener = (
  socket,
  { message },
  {
    chats,
    setChats,
    userId, // active user id
  }
) => {
  // console.log("Socket: new chat listner called!", message, chats);

  if (message.sender.id === userId) {
    window.location.reload(); // FIXME: make an optimal solution later!!!
  }

  // my id
  const myId = getUserIdFromStorage();

  const chatAlreadyExists = chats.find(
    (chat) => chat.id === message.sender.id || chat.id === message.receiver.id
  );
  if (!chatAlreadyExists) {
    setChats([
      ...chats,
      {
        ...(message.sender.id !== myId ? message.sender : message.receiver),
        unreadMessageCount: message.sender.id !== myId ? 1 : 0,
      },
    ]);
  } else {
    if (userId !== message.sender.id && message.sender.id !== myId) {
      // find that chat and update its count, if not on that specific chat
      const chat = chats.find((chat) => chat.id === message.sender.id);
      chat.unreadMessageCount = chat.unreadMessageCount
        ? chat.unreadMessageCount + 1
        : 1;
      setChats([...chats]);
    }
  }
};

/**
 * Add multiple sockets listeners and returns them
 * @param {*} socket
 */
function addListenersToSocket(socket) {
  // add realtime post create socket listener
  const listeners = [
    {
      // post create
      eventName: SOCKET_EVENTS.REALTIME.POST_CREATE,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.REALTIME.POST_CREATE, data);
        store.dispatch(increaseNewPostCount(data.sectionId, data.postId));
      },
    },
    {
      // post delete
      eventName: SOCKET_EVENTS.REALTIME.POST_DELETE,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.REALTIME.POST_DELETE, data);
      },
    },
    {
      // post like & dislike
      eventName: SOCKET_EVENTS.REALTIME.POST_LIKE,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.REALTIME.POST_LIKE, data);
        store.dispatch(triggerSectionPostLikeIncrementAction(data));
        store.dispatch(postDetailHandleLikeIncrement(data));
      },
    },
    {
      // post comment add
      eventName: SOCKET_EVENTS.REALTIME.POST_COMMENT_ADD,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.REALTIME.POST_COMMENT_ADD, data);
        store.dispatch(triggerSectionPostCommentAddAction(data));
        store.dispatch(postDetailHandleAddComment(data));
      },
    },
    {
      // post comment remove
      eventName: SOCKET_EVENTS.REALTIME.POST_COMMENT_REMOVE,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.REALTIME.POST_COMMENT_REMOVE, data);
        store.dispatch(triggerSectionPostCommentRemoveAction(data));
        store.dispatch(postDetailHandleRemoveComment(data));
      },
    },
    {
      // post comment reaction update
      eventName: SOCKET_EVENTS.REALTIME.POST_COMMENT_REACTION_UPDATE,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.REALTIME.POST_COMMENT_REACTION_UPDATE, data);
        store.dispatch(triggerSectionPostCommentReactionUpdateAction(data));
        store.dispatch(postDetailHandleCommentReactionUpdateFromSocket(data));
      },
    },
    {
      // group update
      eventName: SOCKET_EVENTS.REALTIME.GROUP_UPDATE,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.REALTIME.GROUP_UPDATE, data);
        // store.dispatch(updateGroupStateFromSocketPayload(data));
      },
    },
    {
      // section update
      eventName: SOCKET_EVENTS.REALTIME.SECTION_UPDATE,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.REALTIME.SECTION_UPDATE, data);
      },
    },
    {
      // section update
      eventName: SOCKET_EVENTS.REALTIME.KARMAPOINT_INCREMENT,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.REALTIME.KARMAPOINT_INCREMENT, data);
        const { activityType, points } = data; 
        store.dispatch(addKarmaPointAction(points, activityType)); 
      },
    },
    {
      // logout
      eventName: SOCKET_EVENTS.LOGOUT,
      handler: (data) => {
        debugSocket(SOCKET_EVENTS.LOGOUT, data);
        MainUtil.logout();
      },
    },
  ];
  for (const listener of listeners) {
    socket.on(listener.eventName, listener.handler);
    debugSocket("Registered " + listener.eventName + " listener.");
  }
  debugSocket("Registered " + listeners.length + " listeners.");
  return listeners;
}

function removeListenersFromSocket(socket, listeners = []) {
  for (const listener of listeners) {
    socket.off(listener.eventName, listener.handler);
  }
  return true;
}

function debugSocket(eventName, data) {
  // console.log("SOCKET:", eventName, data);
}
